import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { ConfiguracaoRateioResponse } from '@triade/contracts';
import { useConfiguracoesRateio, useReordenarConfiguracoes } from '@/api/configuracoes-rateio';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { ApiError } from '@/lib/api';
import { BotaoRelatorios } from '@/relatorios';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { ConfiguracaoDetalhe, type BuscaConfiguracoes } from './configuracao-detalhe';

/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBuscaConfiguracoes(bruto: Record<string, unknown>): BuscaConfiguracoes {
  const id = typeof bruto.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.id) ? bruto.id : undefined;
  return id ? { id } : {};
}

/** Rotulo do tipo de apropriacao da configuracao (a API so manda o nome do criterio quando e CRITERIO). */
export function rotuloTipoConfiguracao(c: Pick<ConfiguracaoRateioResponse, 'tipo' | 'criterioNome'>): string {
  if (c.tipo === 'MANUAL') return 'Manual';
  if (c.tipo === 'IGUAL') return 'Igual';
  return `Critério: ${c.criterioNome ?? '—'}`;
}

/** Ids na nova ordem, trocando a posicao `indice` com a vizinha `indice + sentido`. */
export function trocarOrdem(ids: string[], indice: number, sentido: -1 | 1): string[] {
  const alvo = indice + sentido;
  if (alvo < 0 || alvo >= ids.length) return ids;
  const nova = [...ids];
  [nova[indice], nova[alvo]] = [nova[alvo]!, nova[indice]!];
  return nova;
}

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/** Tela "Configurações de rateio" (/custos/configuracoes-rateio): sequencia por ordem; registro abre por `?id=`. */
export function ConfiguracoesPage({ busca }: { busca: BuscaConfiguracoes }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('rateio.editar');

  const lista = useConfiguracoesRateio();
  const reordenar = useReordenarConfiguracoes();
  const [erroOrdem, setErroOrdem] = useState<string | null>(null);
  const itens = lista.data ?? [];
  const pagItens = usePaginarLista(itens);

  function mover(indice: number, sentido: -1 | 1) {
    setErroOrdem(null);
    const ids = trocarOrdem(
      itens.map((c) => c.id),
      indice,
      sentido,
    );
    if (ids.every((id, i) => id === itens[i]!.id)) return;
    reordenar.mutate(
      { ids },
      {
        onError: (e) => setErroOrdem(mensagemApi(e)),
      },
    );
  }

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver as configurações de rateio.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar as configurações de rateio" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando configurações de rateio…</Mensagem>;
  else if (itens.length === 0) {
    corpo = (
      <Mensagem papel="status">
        Nenhuma configuração de rateio cadastrada.
        {podeEditar && (
          <>
            {' '}
            <Link to="/custos/configuracoes-rateio" search={{ id: 'novo' }} className="font-medium text-primary hover:underline">
              Cadastre a primeira
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-small" aria-label="Sequência de configurações de rateio">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Ordem</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Centro de apoio</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Tipo</th>
              {podeEditar && <th scope="col" className="px-4 py-2.5 font-medium"><span className="sr-only">Mover</span></th>}
            </tr>
          </thead>
          <tbody>
            {pagItens.visiveis.map((c, indice) => (
              <tr
                key={c.id}
                data-testid="linha-configuracao"
                onClick={() => void navigate({ to: '/custos/configuracoes-rateio', search: (a) => ({ ...a, id: c.id }) })}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', busca.id === c.id && 'bg-line-soft')}
              >
                <td className="tabular px-4 py-3 font-semibold">{c.ordem}</td>
                <td className="px-3 py-3">
                  <span className="font-semibold">{c.centroCodigo}</span> <span className="text-ink-secondary">— {c.centroNome}</span>
                </td>
                <td className="px-3 py-3 text-ink-secondary">{rotuloTipoConfiguracao(c)}</td>
                {podeEditar && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        aria-label={`Subir ordem de ${c.centroNome}`}
                        disabled={indice === 0 || reordenar.isPending}
                        onClick={() => mover(indice, -1)}
                        className="flex size-[30px] items-center justify-center rounded-[8px] border border-input bg-card text-ink-body hover:bg-surface-muted disabled:opacity-40"
                      >
                        <ArrowUp aria-hidden="true" className="size-[15px]" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Descer ordem de ${c.centroNome}`}
                        disabled={indice === itens.length - 1 || reordenar.isPending}
                        onClick={() => mover(indice, 1)}
                        className="flex size-[30px] items-center justify-center rounded-[8px] border border-input bg-card text-ink-body hover:bg-surface-muted disabled:opacity-40"
                      >
                        <ArrowDown aria-hidden="true" className="size-[15px]" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Custos <span aria-hidden="true">/</span> Configurações de rateio
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Configurações de rateio</h1>
        </div>
      </div>

      {erroOrdem && (
        <div role="alert" className="rounded-xl border border-danger-ink/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          Não foi possível alterar a ordem: {erroOrdem}
        </div>
      )}

      <section aria-label="Configurações de rateio" aria-busy={lista.isFetching || reordenar.isPending} className="rounded-2xl border border-border bg-card">
        {corpo}
      </section>

      {busca.id === 'novo' && !podeEditar ? (
        <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-body" role="status">
          Seu acesso não permite cadastrar configurações de rateio.
        </section>
      ) : (
        busca.id && <ConfiguracaoDetalhe key={busca.id} id={busca.id === 'novo' ? undefined : busca.id} busca={busca} podeEditar={podeEditar} />
      )}
      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Nova configuração', onClick: () => void navigate({ to: '/custos/configuracoes-rateio', search: { id: 'novo' } }) } : undefined}
        paginacao={lista.data ? pagItens.paginacao : undefined}
        relatorios={<BotaoRelatorios tela="configuracoes-rateio" />}
      />
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
